import { ReturnOrderItem } from "@/app/interfaces/ReturnOrderItem";
import defaultProductImg from "../../../../img/default-image_420.png";
import { Box, Checkbox, Grid, Typography } from "@mui/material";
import Pagination from "../../shared/pagination";

interface Props {
  items: ReturnOrderItem[];
  selectAll: boolean;

  // pagination inputs
  page: number;
  pageSize: number;
  totalCount: number;

  // event outputs
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;

  onItemChange: (id: number, checked: boolean) => void;
  onSelectAllChange: (checked: boolean) => void;
}

const ReturnItemsGrid: React.FC<Props> = ({
  items,
  selectAll,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onItemChange,
  onSelectAllChange,
}) => {
  const totalItems = totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Checkbox
          checked={selectAll}
          onChange={(e) => onSelectAllChange(e.target.checked)}
          sx={{ mr: 1 }}
        />
        <Typography variant="body1">Select All</Typography>
      </Box>

      {items.map((item, index) => (
        <Box
          key={item.id}
          sx={{
            px: 2,
            py: 2,
            borderBottom:
              index !== items.length - 1 ? "1px solid #e0e0e0" : "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Checkbox
              checked={!!item.isSelected}
              onChange={(e) => onItemChange(item.id, e.target.checked)}
              sx={{ mr: 1 }}
            />
            <Typography
              sx={{
                fontWeight: 500,
                color: "#737373",
                fontSize: "21px",
                fontFamily: '"Montserrat", Verdana, sans-serif',
              }}
            >
              {item.productName}
            </Typography>
          </Box>

          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  component="img"
                  src={
                    item.pictureUrl
                      ? `${process.env.REACT_APP_CDN_URL}/images/general/picture_${item.pictureUrl}`
                      : defaultProductImg
                  }
                  alt={item.productName}
                  sx={{
                    width: "100%",
                    maxWidth: 180,
                    borderRadius: 1,
                    backgroundColor: item.colorHex || "transparent",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography variant="body2">
                  Item: <strong>{item.productSku}</strong>
                </Typography>
                <Typography variant="body2">
                  Color: <strong>{item.color ?? "-"}</strong>
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography variant="body2">
                  Measure: <strong>{item.measure ?? "-"}</strong>
                </Typography>
                <Typography variant="body2">
                  Qty Ord: <strong>{item.quantity}</strong>
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography variant="body2">
                  Qty Available: <strong>{item.availableQuantity}</strong>
                </Typography>
                <Typography variant="body2">
                  UOM: <strong>{item.selectedUom ?? "-"}</strong>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ))}
      {totalItems > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={[10, 50, 100]}
        />
      )}
    </Box>
  );
};

export default ReturnItemsGrid;
